import { getApprovalTerms, ApprovalTerm } from 'api/application'
import { getApprovalTermsSwrKey } from 'api/application/getApprovalTermsSwrKey'
import { AxiosError } from 'axios'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import useSWR from 'swr'

export interface UseApprovalTermsInterface {
    approvalTerms: ApprovalTerm[]
    loading: boolean
    error: Error | null
}

const EMPTY_TERMS: ApprovalTerm[] = []

// Shared raw REST data; each consumer derives its own presentation. A
// consumer unmounting must not abort another consumer's shared request.
export const useApprovalTermsResource = (applicationUuid?: string) =>
    useSWR<ApprovalTerm[], AxiosError>(
        applicationUuid ? getApprovalTermsSwrKey(applicationUuid) : null,
        () => getApprovalTerms(applicationUuid as string),
        {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
            onError: (error) => {
                if (error.code === AxiosError.ERR_CANCELED) return

                OctaneLogger.error(
                    'Error when getting approval terms for application.',
                    { applicationUuid },
                    error,
                )
            },
        },
    )

const useApprovalTerms = (
    applicationUuid: string | undefined,
): UseApprovalTermsInterface => {
    const { data, isLoading, error } = useApprovalTermsResource(applicationUuid)

    return {
        approvalTerms: data ?? EMPTY_TERMS,
        loading: !applicationUuid || isLoading,
        error: error?.code === AxiosError.ERR_CANCELED ? null : (error ?? null),
    }
}

export default useApprovalTerms
